/**
 * Client-side token utilities for authentication management
 */

/**
 * Check if cookies are available in the browser
 * @returns {boolean} True if cookies are enabled and working
 */
export const areCookiesAvailable = () => {
  try {
    // Check if cookies are enabled
    if (!navigator.cookieEnabled) {
      return false;
    }

    // Test basic cookie functionality
    const testCookie = 'auth_test_cookie';
    const testValue = 'test_value_' + Date.now();
    
    document.cookie = `${testCookie}=${testValue}; path=/; max-age=10`;
    const cookiesWork = document.cookie.includes(`${testCookie}=${testValue}`);
    
    // Clean up test cookie
    document.cookie = `${testCookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    
    return cookiesWork;
  } catch (error) {
    console.error('Cookie availability check failed:', error);
    return false;
  }
};

/**
 * Check if we likely have authentication tokens
 * Note: This doesn't validate tokens, just checks for presence
 * @returns {boolean} True if tokens appear to be present
 */
export const hasLikelyTokens = () => {
  try {
    // Check for token-like cookies (without reading httpOnly cookies)
    const cookies = document.cookie;
    
    // Look for patterns that suggest auth cookies exist
    // Note: We can't read httpOnly cookies, but we can check for related cookies
    const hasAuthCookies = cookies.includes('access_token') || 
                          cookies.includes('refresh_token') ||
                          cookies.includes('auth_');
    
    return hasAuthCookies;
  } catch (error) {
    console.error('Token presence check failed:', error);
    return false;
  }
};

/**
 * Get user-friendly error messages for common authentication errors
 * @param {Error|string} error - The error to translate
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (error) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
  const errorCode = error?.response?.status;
  
  // Handle specific error cases
  if (errorCode === 401) {
    if (errorMessage.includes('refresh')) {
      return 'Your session has expired. Please log in again.';
    }
    return 'Authentication failed. Please check your credentials.';
  }
  
  if (errorCode === 403) {
    return 'Access denied. You don\'t have permission to perform this action.';
  }
  
  if (errorCode === 429) {
    return 'Too many login attempts. Please try again later.';
  }
  
  if (errorMessage.includes('network') || errorMessage.includes('Network')) {
    return 'Network error. Please check your connection and try again.';
  }
  
  if (errorMessage.includes('cookie')) {
    return 'Browser cookies are required for authentication. Please enable cookies and try again.';
  }
  
  if (errorMessage.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }
  
  // Default fallback
  return errorMessage || 'An authentication error occurred. Please try again.';
};

/**
 * Create a delay utility for better UX
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after the delay
 */
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry utility with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Promise that resolves with the function result
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s, etc.
      const delayMs = baseDelay * Math.pow(2, attempt);
      console.log(`Retry attempt ${attempt + 1} after ${delayMs}ms`);
      await delay(delayMs);
    }
  }
  
  throw lastError;
};

/**
 * Format time remaining in a user-friendly way
 * @param {number} seconds - Seconds remaining
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (seconds) => {
  if (seconds < 60) {
    return `${seconds} seconds`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
}; 